import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  alpha,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Devices as DevicesIcon,
  DirectionsCar as VehicleIcon,
  Description as DocumentIcon,
  History as HistoryIcon,
  GpsFixed as GpsIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  ExpandLess,
  ExpandMore,
  Info as InfoIcon,
  SimCard as SimCardIcon,
  Router as RouterIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  Folder as FolderIcon,
  FormatListBulleted as ListIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';

const menuGroups = [
  {
    id: 'dashboard',
    text: 'Tableau de Bord',
    icon: <DashboardIcon />,
    path: '/',
    isGroup: false,
  },
  {
    id: 'clients',
    text: 'Clients',
    icon: <PeopleIcon />,
    isGroup: true,
    items: [
      { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
      { text: 'Véhicules', icon: <VehicleIcon />, path: '/vehicles' },
      { text: 'Types Infos', icon: <InfoIcon />, path: '/information-types' },
    ],
  },
  {
    id: 'stock',
    text: 'Stock',
    icon: <DevicesIcon />,
    isGroup: true,
    items: [
      { text: 'Dispositifs GPS', icon: <DevicesIcon />, path: '/devices' },
      { text: 'Cartes SIM', icon: <SimCardIcon />, path: '/sims' },
      { text: 'Opérateurs', icon: <RouterIcon />, path: '/providers' },
    ],
  },
  {
    id: 'documents',
    text: 'Documents',
    icon: <DocumentIcon />,
    isGroup: true,
    items: [
      { text: 'Documents', icon: <DocumentIcon />, path: '/documents' },
      { text: 'Lignes Prédéfinies', icon: <ListIcon />, path: '/predefined-lines' },
      { text: 'Templates PDF', icon: <PdfIcon />, path: '/pdf-templates' },
      { text: 'Types Documents', icon: <CategoryIcon />, path: '/document-types' },
      { text: 'Docs Clients', icon: <FolderIcon />, path: '/client-documents' },
    ],
  },
  {
    id: 'history',
    text: 'Historique',
    icon: <HistoryIcon />,
    path: '/history',
    isGroup: false,
  },
  {
    id: 'settings',
    text: 'Paramètres',
    icon: <SettingsIcon />,
    isGroup: true,
    items: [
      { text: 'Visibilité', icon: <VisibilityIcon />, path: '/visibility-settings' },
      { text: 'Conflits', icon: <WarningIcon />, path: '/conflicts' },
      { text: 'Conflits IMEI', icon: <WarningIcon />, path: '/imei-conflicts' },
      { text: 'Synchronisation', icon: <SettingsIcon />, path: '/settings' },
      { text: 'Entreprise', icon: <SettingsIcon />, path: '/company-settings' },
      { text: 'Prix Dispositifs', icon: <MoneyIcon />, path: '/device-prices' },
    ],
  },
];

function GroupedNavigation() {
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);

  const handleGroupClick = (groupId) => {
    setOpenGroup(openGroup === groupId ? null : groupId);
  };

  const isPathActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (group) => {
    if (!group.isGroup) {
      return isPathActive(group.path);
    }
    return group.items.some(item => isPathActive(item.path));
  };

  const renderMenuItem = (item, isSubItem = false) => {
    const isActive = isPathActive(item.path);
    
    return (
      <ListItem key={item.path} disablePadding sx={{ mb: isSubItem ? 0 : 0.5 }}>
        <ListItemButton
          component={Link}
          to={item.path}
          selected={isActive}
          sx={{
            borderRadius: 2,
            mx: isSubItem ? 0 : 1.5,
            ml: isSubItem ? 4 : 1.5,
            color: 'grey.300',
            '& .MuiListItemIcon-root': {
              color: 'grey.400',
            },
            '&:hover': {
              bgcolor: alpha('#fff', 0.08),
            },
            '&.Mui-selected': {
              bgcolor: alpha('#fff', 0.12),
              '& .MuiListItemIcon-root': {
                color: 'primary.light',
              },
              '& .MuiListItemText-primary': {
                color: 'white',
                fontWeight: 600,
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText 
            primary={item.text} 
            primaryTypographyProps={{ 
              fontSize: isSubItem ? '0.85rem' : '0.9rem',
              noWrap: true,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderGroupItem = (group) => {
    const isActive = isGroupActive(group);
    const isOpen = openGroup === group.id;
    const isHovered = hoveredGroup === group.id;

    return (
      <Box key={group.id}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => handleGroupClick(group.id)}
            onMouseEnter={() => setHoveredGroup(group.id)}
            onMouseLeave={() => setHoveredGroup(null)}
            selected={isActive}
            sx={{
              borderRadius: 2,
              mx: 1.5,
              color: 'grey.300',
              position: 'relative',
              '& .MuiListItemIcon-root': {
                color: 'grey.400',
              },
              '&:hover': {
                bgcolor: alpha('#fff', 0.08),
              },
              '&.Mui-selected': {
                bgcolor: alpha('#fff', 0.12),
                '& .MuiListItemIcon-root': {
                  color: 'primary.light',
                },
                '& .MuiListItemText-primary': {
                  color: 'white',
                  fontWeight: 600,
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{group.icon}</ListItemIcon>
            <ListItemText 
              primary={group.text} 
              primaryTypographyProps={{ 
                fontSize: '0.9rem',
                noWrap: true,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          {/* Horizontal submenu on hover */}
          {isHovered && (
            <Paper
              onMouseEnter={() => setHoveredGroup(group.id)}
              onMouseLeave={() => setHoveredGroup(null)}
              sx={{
                position: 'absolute',
                left: '100%',
                top: 0,
                ml: 1,
                minWidth: 200,
                maxWidth: 250,
                zIndex: 1300,
                bgcolor: 'background.paper',
                boxShadow: 3,
                borderRadius: 2,
                py: 1,
                overflow: 'hidden',
              }}
            >
              <List disablePadding>
                {group.items.map((item) => {
                  const isActive = isPathActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        selected={isActive}
                        sx={{
                          mx: 1,
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText 
                          primary={item.text} 
                          primaryTypographyProps={{ 
                            fontSize: '0.85rem',
                            noWrap: true,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}
        </ListItem>

        {/* Collapsible submenu */}
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {group.items.map((item) => renderMenuItem(item, true))}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <List sx={{ mt: 1 }}>
      {menuGroups.map((group) => {
        if (group.isGroup) {
          return renderGroupItem(group);
        } else {
          return renderMenuItem(group);
        }
      })}
    </List>
  );
}

export default GroupedNavigation;
